import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {
    ItProjectDto,
    ItProjectsClient,
    ItProjectStatus,
    ItProjectType,
    type IItProjectDto,
} from "@lib/ShiOneClient";
import {ApiClientConfig} from "../../../../../auth/ApiClientConfig";
import {CreateItProjectRequest, ItProject} from "../types";
import { Toast } from "shared-ui/src/components/Toast";
import { toGuidOrUndefined } from "shared-ui/src/functions/isGuid";
import { fetchLinkedCounts } from "../api/itProjectsLinkedCountsClient";
import { fetchLinkedVariantAggregateCounts } from "../api/itProjectVariantAggregateCountsClient";

export const itProjectsQueryKey = ["it-projects"] as const;
export const itProjectsListQueryKey = (page: number, pageSize: number, search: string) =>
    [...itProjectsQueryKey, "list", page, pageSize, search] as const;
export const itProjectDetailQueryKey = (projectId: string) =>
    [...itProjectsQueryKey, "detail", projectId] as const;
export const itProjectsStatusCountsQueryKey = ["it-projects-status-counts"] as const;

export interface PagedItProjects {
    items: ItProject[];
    totalCount: number;
    page: number;
    pageSize: number;
}

const itProjectsClient = new ItProjectsClient(
    ApiClientConfig,
    import.meta.env.API_ENDPOINT,
);


// Parses YYYY-MM-DD as local midnight — new Date("YYYY-MM-DD") parses as UTC and
// shifts the day when NSwag's formatDate serializes via local getters.
export function toLocalDate(dateString: string): Date {
    const [year, month, day] = dateString.split("-").map(Number);
    return new Date(year, month - 1, day);
}

function toDateInputValue(value?: Date | string): string {
    if (!value) {
        return "";
    }

    const resolvedDate = typeof value === "string" ? new Date(value) : value;

    return Number.isNaN(resolvedDate.getTime())
        ? ""
        : resolvedDate.toISOString().split("T")[0];
}

function emptyCounts(): ItProject["counts"] {
    return {
        integrationOrders: 0,
        orders: 0,
        quotes: 0,
    };
}

async function getProjectCounts(projectId?: string): Promise<ItProject["counts"]> {
    if (!projectId) {
        return emptyCounts();
    }

    const [linkedCountsResult, aggregateCountsResult] = await Promise.allSettled([
        fetchLinkedCounts(projectId),
        fetchLinkedVariantAggregateCounts(projectId),
    ]);

    const linkedCounts =
        linkedCountsResult.status === "fulfilled" ? linkedCountsResult.value : undefined;
    const aggregateCounts =
        aggregateCountsResult.status === "fulfilled"
            ? aggregateCountsResult.value
            : undefined;

    return {
        integrationOrders: aggregateCounts?.integrationOrders ?? 0,
        orders: linkedCounts?.purchaseOrders ?? 0,
        quotes: linkedCounts?.quotes ?? 0,
    };
}

function mapDtoToProject(
    dto: IItProjectDto,
    index: number,
    counts: ItProject["counts"] = emptyCounts(),
): ItProject {
    return {
        id: dto.itProjectId ?? `missing-project-id-${index}`,
        title: dto.title ?? "",
        description: dto.description ?? "",
        projectType: dto.itProjectType ?? ItProjectType.Other,
        customProjectTypeName: dto.customProjectTypeName ?? undefined,
        status: dto.status ?? ItProjectStatus.Planning,
        customerProjectId: dto.customerItProjectId ?? "",
        shiLeadId: dto.shiLeadId ?? "",
        customerLeadId: dto.customerLeadId ?? "",
        targetStartDate: toDateInputValue(dto.targetStartDate),
        targetCompletionDate: toDateInputValue(dto.targetEndDate),
        totalInvestments: dto.totalInvestments ?? 0,
        counts,
    };
}

function mapCreateRequestToDto(request: CreateItProjectRequest): ItProjectDto {
    return new ItProjectDto({
        itProjectId: undefined,
        title: request.title.trim(),
        description: request.description.trim() || undefined,
        customerItProjectId: request.customerProjectId.trim() || undefined,
        itProjectType: request.projectType,
        // Only send the custom name when the type is actually Other; clear it otherwise.
        customProjectTypeName:
            request.projectType === ItProjectType.Other
                ? request.customProjectTypeName?.trim() || undefined
                : undefined,
        status: request.status,
        targetStartDate: request.targetStartDate ? toLocalDate(request.targetStartDate) : undefined,
        targetEndDate: request.targetCompletionDate ? toLocalDate(request.targetCompletionDate) : undefined,
        shiLeadId: toGuidOrUndefined(request.shiLeadId),
        customerLeadId: toGuidOrUndefined(request.customerLeadId),
    });
}
export function useItProjectsPage(page: number, pageSize: number, search: string) {
    const normalizedSearch = search.trim();

    return useQuery({
        queryKey: itProjectsListQueryKey(page, pageSize, normalizedSearch),
        queryFn: async () => {
            const response = await itProjectsClient.getProjectsListByAccount(
                page,
                pageSize,
                normalizedSearch || undefined,
            );

            const items = await Promise.all(
                (response.items ?? []).map(async (project, index) =>
                    mapDtoToProject(
                        project,
                        index,
                        await getProjectCounts(project.itProjectId),
                    ),
                ),
            );

            return {
                items,
                totalCount: response.totalCount ?? 0,
                page: response.page ?? page,
                pageSize: response.pageSize ?? pageSize,
            } satisfies PagedItProjects;
        },
    });
}

export function useItProjectById(projectId: string | undefined) {
    return useQuery({
        queryKey: itProjectDetailQueryKey(projectId ?? ""),
        enabled: Boolean(projectId),
        queryFn: async () => {
            if (!projectId) {
                return undefined;
            }

            const result = await itProjectsClient.getByShiProjectId(projectId);
            return mapDtoToProject(result, 0);
        },
    });
}

export function useCreateItProject() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (request: CreateItProjectRequest) => {
            const created = await itProjectsClient.create(
                mapCreateRequestToDto(request),
            );
            return mapDtoToProject(created, 0);
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({queryKey: itProjectsQueryKey});
            await queryClient.invalidateQueries({queryKey: itProjectsStatusCountsQueryKey});
            Toast("Project created successfully");
        },
        onError: () => {
            Toast("Failed to create project. Please try again.", true);
        },
    });
}

export function useUpdateItProject() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
                               projectId,
                               request,
                           }: {
            projectId: string;
            request: CreateItProjectRequest;
        }) => {
            const result = await itProjectsClient.update(
                projectId,
                mapCreateRequestToDto(request),
            );

            return mapDtoToProject(result, 0);
        },
        onSuccess: async (updated) => {
            await queryClient.invalidateQueries({queryKey: itProjectsQueryKey});
            await queryClient.invalidateQueries({queryKey: itProjectsStatusCountsQueryKey});
            await queryClient.invalidateQueries({
                queryKey: itProjectDetailQueryKey(updated.id),
            });
            Toast("Project updated successfully");
        },
        onError: () => {
            Toast("Failed to update project. Please try again.", true);
        },
    });
}

export function useDeleteItProject() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (projectId: string) => {
            await itProjectsClient.deleteItProject(projectId);
        },
        onSuccess: async (_value, projectId) => {
            queryClient.removeQueries({queryKey: itProjectDetailQueryKey(projectId)});
            await queryClient.invalidateQueries({queryKey: itProjectsQueryKey});
            await queryClient.invalidateQueries({queryKey: itProjectsStatusCountsQueryKey});
            Toast("Project deleted successfully");
        },
        onError: () => {
            Toast("Failed to delete project. Please try again.", true);
        },
    });
}